import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, MapPin, Clock, Briefcase, Users, Heart, Coffee, TrendingUp, Award } from 'lucide-react';
import { useTheme } from '../../contexts/Themecontext';
import { careersAPI } from '../../services/api';

const Careers = () => {
  const [scrollY, setScrollY] = useState(0);
  const [selectedDepartment, setSelectedDepartment] = useState('All');
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { theme } = useTheme();

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    fetchJobs();
  }, [selectedDepartment]);

  const fetchJobs = async () => {
    setLoading(true);
    setError('');
    
    try {
      // Wrap the string in an object. 
      // If 'All', we send an empty object so no filter is applied.
      const params = selectedDepartment === 'All' ? {} : { department: selectedDepartment };
      
      const data = await careersAPI.getJobs(params);
      setJobs(data || []);
    } catch (err) {
      console.error('Error fetching jobs:', err);
      setError('Failed to load jobs. Please try again later.');
      setJobs([]);
    } finally {
      setLoading(false);
    }
  };

  const getThemeClasses = () => {
    if (theme === 'light') {
      return {
        bg: 'bg-gradient-to-br from-blue-50 via-white to-purple-50',
        text: 'text-gray-900',
        subtext: 'text-gray-600',
        cardBg: 'bg-white',
        cardBorder: 'border-gray-200',
        accent: 'from-blue-600 to-purple-600',
        accentText: 'text-blue-600',
        buttonActive: 'bg-blue-600 text-white',
        buttonInactive: 'bg-gray-100 text-gray-700 hover:bg-gray-200',
      };
    }
    if (theme === 'dark') {
      return {
        bg: 'bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900',
        text: 'text-white',
        subtext: 'text-gray-300',
        cardBg: 'bg-slate-800',
        cardBorder: 'border-slate-700',
        accent: 'from-cyan-600 to-blue-600',
        accentText: 'text-cyan-400',
        buttonActive: 'bg-cyan-600 text-white',
        buttonInactive: 'bg-slate-700 text-gray-300 hover:bg-slate-600',
      };
    }
    return {
      bg: 'bg-gradient-to-br from-slate-950 via-indigo-950 to-purple-950',
      text: 'text-white',
      subtext: 'text-gray-200',
      cardBg: 'bg-gradient-to-br from-slate-900/50 to-purple-900/50',
      cardBorder: 'border-purple-500/30',
      accent: 'from-purple-600 to-pink-600',
      accentText: 'text-purple-400',
      buttonActive: 'bg-purple-600 text-white',
      buttonInactive: 'bg-slate-800 text-gray-300 hover:bg-slate-700',
    };
  };

  const classes = getThemeClasses();

  const benefits = [
    {
      icon: <Heart className="w-6 h-6" />,
      title: 'Health & Wellness',
      description: 'Comprehensive medical, dental, and vision coverage',
    },
    {
      icon: <Coffee className="w-6 h-6" />,
      title: 'Work-Life Balance',
      description: 'Flexible hours, remote work options, unlimited PTO',
    },
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: 'Career Growth',
      description: 'Professional development budget and mentorship programs',
    },
    {
      icon: <Award className="w-6 h-6" />,
      title: 'Equity & Bonuses',
      description: 'Competitive salary, stock options, and performance bonuses',
    },
  ];

  const departments = ['All', 'Research', 'Engineering', 'Operations'];

  return (
    <div className="min-h-screen overflow-hidden">
      <style>{`
        @keyframes slide-in-up {
          from { transform: translateY(50px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes scale-in {
          from { transform: scale(0.9); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
        .animate-slide-in-up {
          animation: slide-in-up 0.8s ease-out forwards;
        }
        .animate-fade-in {
          animation: fade-in 1s ease-out forwards;
        }
        .animate-scale-in {
          animation: scale-in 0.6s ease-out forwards;
        }
      `}</style>

      {/* Hero Section */}
      <section className={`relative min-h-[60vh] flex items-center ${classes.bg} overflow-hidden`}>
        <div className="absolute inset-0 overflow-hidden">
          {[...Array(40)].map((_, i) => (
            <div
              key={i}
              className={`absolute w-1 h-1 ${theme === 'light' ? 'bg-blue-400' : theme === 'dark' ? 'bg-cyan-400' : 'bg-purple-400'} rounded-full opacity-30`}
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animation: `float ${5 + Math.random() * 5}s ease-in-out infinite`,
                animationDelay: `${Math.random() * 2}s`,
              }}
            />
          ))}
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-20 text-center">
          <div className="animate-slide-in-up">
            <h1 className={`text-4xl sm:text-5xl lg:text-6xl font-bold ${classes.text} mb-6`}>
              Build the{' '}
              <span className={`bg-gradient-to-r ${classes.accent} bg-clip-text text-transparent`}>
                Quantum Future
              </span>
            </h1>
            <p className={`text-lg sm:text-xl ${classes.subtext} max-w-3xl mx-auto mb-8`}>
              Join a world-class team of researchers, engineers, and innovators working on the most
              exciting challenges in quantum computing, semiconductors, and superconducting systems.
            </p>
            <div className={`inline-flex items-center gap-2 ${classes.cardBg} px-4 sm:px-6 py-3 rounded-full border ${classes.cardBorder}`}>
              <Users className={`w-5 h-5 ${classes.accentText}`} />
              <span className={`${classes.text} text-sm sm:text-base font-semibold`}>
                {jobs.length} Open Position{jobs.length !== 1 ? 's' : ''}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className={`py-16 sm:py-24 ${theme === 'light' ? 'bg-white' : theme === 'dark' ? 'bg-slate-900' : 'bg-slate-950'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16 animate-fade-in">
            <h2 className={`text-3xl sm:text-4xl font-bold ${classes.text} mb-4`}>
              Why Join GLYQIX?
            </h2>
            <p className={`text-lg ${classes.subtext}`}>
              We invest in our team's success and well-being
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className={`${classes.cardBg} backdrop-blur-md rounded-2xl p-6 border ${classes.cardBorder} text-center animate-scale-in`}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className={`inline-flex items-center justify-center w-16 h-16 rounded-xl bg-gradient-to-r ${classes.accent} text-white mb-4`}>
                  {benefit.icon}
                </div>
                <h3 className={`text-xl font-bold ${classes.text} mb-2`}>{benefit.title}</h3>
                <p className={`${classes.subtext} text-sm`}>{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Open Positions */}
      <section className={`py-16 sm:py-24 ${classes.bg}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 animate-fade-in">
            <h2 className={`text-3xl sm:text-4xl font-bold ${classes.text} mb-4`}>
              Open Positions
            </h2>
            <p className={`text-lg ${classes.subtext} mb-8`}>
              Find your next opportunity
            </p>

            {/* Department Filter */}
            <div className="flex flex-wrap justify-center gap-3 mb-8">
              {departments.map((dept) => (
                <button
                  key={dept}
                  onClick={() => setSelectedDepartment(dept)}
                  className={`px-4 sm:px-6 py-2 sm:py-3 rounded-lg font-semibold text-sm sm:text-base transition-all ${
                    selectedDepartment === dept ? classes.buttonActive : classes.buttonInactive
                  }`}
                >
                  {dept}
                </button>
              ))}
            </div>
          </div>

          {loading && (
            <div className="text-center py-12">
              <div className="inline-block w-12 h-12 border-4 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
              <p className={`mt-4 ${classes.subtext}`}>Loading jobs...</p>
            </div>
          )}

          {error && (
            <div className="text-center py-12">
              <p className="text-red-500">{error}</p>
              <button
                onClick={fetchJobs}
                className="mt-4 px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
              >
                Try Again
              </button>
            </div>
          )}

          {!loading && !error && (
            <div className="space-y-4 sm:space-y-6">
              {jobs.map((job, index) => (
                <div
                  key={job.id}
                  className={`${classes.cardBg} backdrop-blur-md rounded-xl p-4 sm:p-6 border ${classes.cardBorder} hover:border-opacity-50 transition-all animate-slide-in-up`}
                  style={{ animationDelay: `${index * 0.05}s` }}
                >
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                    <div className="flex-1">
                      <h3 className={`text-lg sm:text-xl font-bold ${classes.text} mb-2`}>
                        {job.title}
                      </h3>
                      <p className={`${classes.subtext} text-sm sm:text-base mb-4`}>
                        {job.description}
                      </p>
                      <div className="flex flex-wrap gap-3 text-xs sm:text-sm">
                        <span className={`flex items-center gap-1 ${classes.subtext}`}>
                          <Briefcase className="w-4 h-4" />
                          {job.department}
                        </span>
                        <span className={`flex items-center gap-1 ${classes.subtext}`}>
                          <MapPin className="w-4 h-4" />
                          {job.location}
                        </span>
                        <span className={`flex items-center gap-1 ${classes.subtext}`}>
                          <Clock className="w-4 h-4" />
                          {job.type}
                        </span>
                      </div>
                    </div>
                    <Link
                      to={`/careers/job/${job.id}`}
                      className={`inline-flex items-center justify-center px-6 py-3 rounded-xl font-semibold bg-gradient-to-r ${classes.accent} text-white hover:shadow-lg transition-all whitespace-nowrap self-start lg:self-center`}
                    >
                      View Details
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}

          {!loading && !error && jobs.length === 0 && (
            <div className={`text-center ${classes.subtext} py-12`}>
              <p className="text-lg">No positions found in this department.</p>
              <p className="mt-2">Check back soon for new opportunities!</p>
            </div>
          )}
        </div>
      </section>

      {/* Culture Section */}
      <section className={`py-16 sm:py-24 ${theme === 'light' ? 'bg-white' : theme === 'dark' ? 'bg-slate-900' : 'bg-slate-950'}`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center animate-fade-in">
            <h2 className={`text-3xl sm:text-4xl font-bold ${classes.text} mb-6`}>
              Our Culture
            </h2>
            <p className={`text-base sm:text-lg ${classes.subtext} mb-8`}>
              At GLYQIX, we foster a collaborative environment where innovation thrives. Our team comprises
              world-class researchers, engineers, and visionaries united by a shared passion for advancing
              quantum technology. We value diversity, creativity, and the pursuit of scientific excellence.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 sm:gap-8 mt-12">
              <div className={`${classes.cardBg} rounded-xl p-4 sm:p-6 border ${classes.cardBorder}`}>
                <div className={`text-2xl sm:text-3xl font-bold ${classes.accentText} mb-2`}>15+</div>
                <div className={`text-xs sm:text-sm ${classes.subtext}`}>Countries</div>
              </div>
              <div className={`${classes.cardBg} rounded-xl p-4 sm:p-6 border ${classes.cardBorder}`}>
                <div className={`text-2xl sm:text-3xl font-bold ${classes.accentText} mb-2`}>50%</div>
                <div className={`text-xs sm:text-sm ${classes.subtext}`}>PhDs</div>
              </div>
              <div className={`${classes.cardBg} rounded-xl p-4 sm:p-6 border ${classes.cardBorder}`}>
                <div className={`text-2xl sm:text-3xl font-bold ${classes.accentText} mb-2`}>40%</div>
                <div className={`text-xs sm:text-sm ${classes.subtext}`}>Women in STEM</div>
              </div>
              <div className={`${classes.cardBg} rounded-xl p-4 sm:p-6 border ${classes.cardBorder}`}>
                <div className={`text-2xl sm:text-3xl font-bold ${classes.accentText} mb-2`}>95%</div>
                <div className={`text-xs sm:text-sm ${classes.subtext}`}>Satisfaction</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className={`py-16 sm:py-24 ${classes.bg}`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center animate-fade-in">
          <h2 className={`text-3xl sm:text-4xl font-bold ${classes.text} mb-6`}>
            Don't See a Perfect Fit?
          </h2>
          <p className={`text-lg ${classes.subtext} mb-8`}>
            We're always looking for exceptional talent. Send us your resume and let us know how
            you'd like to contribute to the quantum revolution.
          </p>
          <Link
            to="/contact"
            className={`inline-flex items-center px-8 py-4 rounded-xl text-white font-semibold bg-gradient-to-r ${classes.accent} hover:shadow-lg transition-all`}
          >
            Get in Touch
            <ArrowRight className="ml-2 w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Careers;